/**
 * fetchJsonInWorker — fetch + JSON.parse a URL on a shared web worker, so the
 * main thread never pays the parse cost of the large map feeds (hotspots,
 * road closures, hotspot-history snapshots). See jsonFetchWorker.ts.
 *
 * A single module-level worker is reused across all callers and requests are
 * correlated by id. If workers are unavailable (SSR, jsdom tests, an old
 * browser) or the worker crashes, it transparently falls back to a normal
 * main-thread fetch + parse so behaviour is never worse than before.
 */

interface WorkerResponse {
  id: number;
  ok: boolean;
  data?: unknown;
  error?: string;
  status?: number;
}

type Pending = { resolve: (value: unknown) => void; reject: (err: Error) => void };

let worker: Worker | null = null;
let workerUnavailable = false;
let nextId = 1;
const pending = new Map<number, Pending>();

function getWorker(): Worker | null {
  if (workerUnavailable) return null;
  if (worker) return worker;
  if (typeof Worker === 'undefined') {
    workerUnavailable = true;
    return null;
  }
  try {
    worker = new Worker(new URL('./jsonFetchWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const { id, ok, data, error } = e.data;
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      if (ok) p.resolve(data);
      else p.reject(new Error(error || 'worker fetch failed'));
    };
    worker.onerror = () => {
      // Worker crashed. Reject everything in flight and disable the worker path
      // — subsequent calls fall back to the main thread.
      workerUnavailable = true;
      for (const [, p] of pending) p.reject(new Error('json worker error'));
      pending.clear();
      try { worker?.terminate(); } catch { /* ignore */ }
      worker = null;
    };
    return worker;
  } catch {
    workerUnavailable = true;
    return null;
  }
}

async function mainThreadFallback<T>(url: string, headers?: Record<string, string>): Promise<T> {
  const response = await fetch(url, headers ? { headers } : undefined);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json() as Promise<T>;
}

/**
 * Fetch `url` and parse the JSON body on the worker. Resolves with the parsed
 * value; rejects on network/HTTP/parse errors (same as a failed fetch+json).
 */
export function fetchJsonInWorker<T>(url: string, headers?: Record<string, string>): Promise<T> {
  const w = getWorker();
  if (!w) return mainThreadFallback<T>(url, headers);

  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    try {
      w.postMessage({ id, url, headers });
    } catch {
      // postMessage failed (e.g. worker died between getWorker and now) —
      // fall back to the main thread for this request.
      pending.delete(id);
      mainThreadFallback<T>(url, headers).then(resolve, reject);
    }
  });
}
